import { Head } from "vite-react-ssg";
import { useLocation } from "react-router-dom";
import { seoMeta, type SeoEntry } from "@/data/seoMeta";
import { clampDescription } from "@/lib/seo";

const SITE_ORIGIN = "https://www.tiego.com";
const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/z4VhhKdeISaIEPzkheef2Yt3xpt1/social-images/social-1776666995425-Tiego.webp";

type Lang = "sv" | "en";

interface SEOProps {
  /** Explicit route-nyckel i seoMeta (t.ex. "/tga"). Default: härled från pathname. */
  routeKey?: string;
  fallbackTitle?: string;
  fallbackDescription?: string;
}

/**
 * Renderar deklarativ SEO-metadata via vite-react-ssg's <Head>.
 *
 * - title, meta description, canonical, hreflang, OG, Twitter
 * - <html lang> sätts via Helmet's html-attribut, så det hamnar i statisk HTML
 *   per språk
 *
 * Innehållet kommer från src/data/seoMeta.ts. Renderas typiskt via LanguageLayout
 * så att alla språk-prefixade routes får SEO automatiskt.
 */
export function SEO({ routeKey, fallbackTitle, fallbackDescription }: SEOProps = {}) {
  const { pathname } = useLocation();

  const lang: Lang = pathname.startsWith("/en") ? "en" : "sv";

  // Strip /sv eller /en så vi får route-nyckeln (t.ex. "/tga", "/", "/kommuner")
  const key =
    routeKey ?? (pathname.replace(/^\/(sv|en)/, "").replace(/\/$/, "") || "/");

  const entry: SeoEntry | undefined = seoMeta[key];
  const meta = entry?.[lang];

  const title = meta?.title || fallbackTitle || "Tiego";
  // Spärr: meta description får aldrig överstiga 155 tecken (SEO-krav), oavsett seoMeta-text.
  const description = clampDescription(meta?.description || fallbackDescription || "");

  const canonicalPath = `/${lang}${key === "/" ? "" : key}`;
  const canonicalUrl = `${SITE_ORIGIN}${canonicalPath}`;

  const altPath = (l: Lang) => `${SITE_ORIGIN}/${l}${key === "/" ? "" : key}`;

  return (
    <Head>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="sv" href={altPath("sv")} />
      <link rel="alternate" hrefLang="en" href={altPath("en")} />
      <link rel="alternate" hrefLang="x-default" href={altPath("sv")} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={lang === "sv" ? "sv_SE" : "en_US"} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
    </Head>
  );
}

export default SEO;
